import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	ElementRef,
	ViewChild,
	WritableSignal,
	computed,
	inject,
	signal,
	OnInit,
	AfterViewInit,
	effect,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AuRightSidebarService } from '@app/core/services/au-right-sidebar.service';
import { IconSnackBarService } from '@app/core/services/icon-snack-bar.service';
import { Observable, take } from 'rxjs';
import { OperationalMessagesService } from '../../services/operational-messages.service';
import { IMassMessage, IMessageText } from '../../interfaces/messages.interfaces';
import { Store } from '@ngrx/store';
import { selectViewMessage, messageTextData } from '../../state/messages.selectors';
import {
	clearMessageExternalDate,
	clearViewMessage,
	setMessageExternalDate,
} from '../../state/messages.action';
import { getCurrentBuilding } from '@app/pages/my-building/state/my-bulding.selectors';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { getContactsList } from '@app/pages/contacts/state/contacts.selectors';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ContactsService } from '@app/pages/contacts/services/contacts.service';
import { AuPipesModule } from '@app/shared/pipes/au-pipes.module';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { RightSidebarNavigationService } from '@app/layout/au-right-sidebar/right-sidebar-navigation.service';
import { RightSidebarAliasesEnum } from '@app/layout/au-right-sidebar/au-right-sidebar-component-aliases';
import { AuUtilsFunctions } from '@app/shared';
import { ACTIVE_PAGE_CONFIG } from '@app/pages/cmms/configs/active-page.config';
import { FilesService } from '@app/pages/cmms/services/files.service';
import { IDroppedFile } from '@app/shared/ui-components/au-file-loader/au-file-loader.interfaces';
import { fileTypesConfig } from '@app/shared/ui-components/au-file-loader/au-file-loader.config';
import { IFileEntity } from '@app/pages/cmms/interfaces/files.interfaces';
import { TableType } from '@app/pages/cmms/types';
import { SetRightSidebarComponentContext } from '@app/layout/state/layout.actions';
import { sidebarHeaders } from '@app/pages/cmms/configs/cmms-table-configs';
import { ICMMSLocation } from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { groupContacts } from '@app/pages/contacts/state/contacts.utils';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { AuFileLoaderComponent } from '@app/shared/ui-components/au-file-loader/au-file-loader.component';

interface InitialContent {
	recipients?: { email: string; name: string }[];
	missingEmails?: number;
	location?: ICMMSLocation;
}

@Component({
	selector: 'new-message',
	templateUrl: './new-message.component.html',
	styleUrls: ['./new-message.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		MatFormFieldModule,
		MatInputModule,
		MatFormFieldModule,
		ReactiveFormsModule,
		MatIconModule,
		MatButtonModule,
		MatAutocompleteModule,
		AuPipesModule,
		AuFileLoaderComponent,
		NgxTolgeeModule,
	],
})
export class NewMessageComponent implements OnInit, AfterViewInit {
	@ViewChild('recipientsInput') recipientsInput: ElementRef;
	@ViewChild('subjectInput') subjectInput: ElementRef;

	private readonly fb = inject(FormBuilder);
	private readonly destroyRef = inject(DestroyRef);
	private readonly notify = inject(IconSnackBarService);
	private readonly operationalMessagesService = inject(OperationalMessagesService);
	private readonly sidebarNavigation = inject(RightSidebarNavigationService);
	private readonly filesService = inject(FilesService);
	readonly sidebarService = inject(AuRightSidebarService);
	readonly store$ = inject(Store);
	readonly contactsService = inject(ContactsService);
	readonly translateService = inject(TranslateService);

	isFullscreen$: Observable<boolean>;
	newMessageForm: FormGroup;
	recipients: AbstractControl<string, string>;

	currentBuilding = this.store$.selectSignal(getCurrentBuilding);
	viewingMessage = this.store$.selectSignal(selectViewMessage);
	maxSubjectLength = 100;
	emails: WritableSignal<{ email: string; name: string }[]> = signal([]);
	onFloatLabel = computed(() => (this.emails().length ? 'always' : 'auto'));
	contactList = this.store$.selectSignal(getContactsList);
	contactGroup = computed(() => {
		return groupContacts(this.contactList(), 'location');
	});
	newMessage: boolean = true;

	initialContent: InitialContent = this.sidebarService.sidebarConfig.initialContent || {};

	buildingNamePrefix = computed(() => {
		const buildingName = this.currentBuilding()?.name
			? this.currentBuilding()?.name
			: this.initialContent.location?.name;
		return buildingName ? `${buildingName}: ` : '';
	});

	emailWarning = computed(
		() =>
			this.initialContent.missingEmails &&
			this.translateService.instant('missing_email_addresses', {
				count: this.initialContent.missingEmails,
			})
	);
	searchText = signal('');
	constructor() {
		effect(() => {
			const noEmails = !this.emails().length && !this.newMessageForm.value.recipients;
			this.recipients.setValidators(
				noEmails ? [Validators.required, Validators.email] : Validators.email
			);
			this.recipients.setErrors(noEmails ? { mismatched: true } : null);
		});
	}

	ngOnInit() {
		this.newMessageForm = this.fb.group({
			recipients: ['', [Validators.required, Validators.email]],
			subject: [this.buildingNamePrefix(), [Validators.required]],
			text: [''],
			files: [[]],
			locations: [[]],
		});
		this.recipients = this.newMessageForm.get('recipients');
		this.trackSidebarSubmitAction();
		this.trackSidebarCloseAction();
		this.contactsService.handleContacts(this.currentBuilding()?.locationId);
		if (this.viewingMessage()) {
			this.populateForm();
			this.newMessageForm.disable();
			this.newMessage = false;
		}

		if (this.initialContent.location || this.currentBuilding()) {
			this.newMessageForm.patchValue({
				locations: [
					this.currentBuilding() ? this.currentBuilding() : this.initialContent.location,
				],
			});
		}

		if (this.initialContent.recipients) {
			this.emails.set(this.initialContent.recipients);
		}

		this.newMessageForm.controls.recipients.valueChanges.subscribe(
			(value: string | { email: string; firstName: string; lastName: string }) => {
				this.recipientStringToEmail(value);
			}
		);
		this.store$
			.select(messageTextData)
			.subscribe((messageText: IMessageText) => this.populateMessageText(messageText));
	}

	ngAfterViewInit() {
		this.getFormValueFromStore();
		setTimeout(() => {
			if (!this.emails().length) {
				return this.recipientsInput.nativeElement.focus();
			}
			this.subjectInput.nativeElement.focus();
		}, 300);
	}

	populateForm() {
		this.newMessageForm.patchValue({
			recipients: ` ${this.viewingMessage().recipients}`,
			subject: ` ${this.viewingMessage().subject}`,
			text: this.viewingMessage().text,
			files: this.viewingMessage().attachments,
		});
	}

	onSendMessage() {
		let recipients = this.emails()
			.map(contact => contact.email)
			.join(',');

		if (this.newMessageForm.value.recipients) {
			recipients = recipients
				? this.newMessageForm.value.recipients + ', ' + recipients
				: this.newMessageForm.value.recipients;
			if (!this.newMessageForm.value.recipients.includes('@')) {
				return this.notify.error(this.translateService.instant('error_invalid_email'));
			}
			if (this.isEmailInRecipients(this.newMessageForm.value.recipients)) {
				recipients = this.newMessageForm.value.recipients;
			}
		}
		this.operationalMessagesService.add(
			this.newMessageForm.value as IMassMessage,
			recipients
		);
		this.operationalMessagesService
			.sendOperationalMessage(
				this.newMessageForm.value as IMassMessage,
				this.currentBuilding()
			)
			.subscribe(_ => {
				this.sidebarService.closeAndClearPortal();
				this.store$.dispatch(clearMessageExternalDate());
				this.notify.success(this.translateService.instant('success_message_sent'));
			});
	}

	trackSidebarSubmitAction() {
		this.sidebarService.sidebarActionBtnClicked$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				if (!this.newMessageForm.valid) {
					return this.notify.warning(
						this.translateService.instant('required_fields_warning')
					);
				}
				this.onSendMessage();
			});
	}
	trackSidebarCloseAction() {
		this.sidebarService.sidebarIsClosing$.pipe(take(1)).subscribe(() => {
			this.sidebarService.closeAndClearPortal();
			this.store$.dispatch(clearViewMessage());
			this.store$.dispatch(clearMessageExternalDate());
		});
	}

	onBackspacePressed() {
		if (this.recipients.value === '' && this.emails().length > 0) {
			this.emails.update(emails => emails.slice(0, -1));
		}
	}

	onEnterPressed() {
		const recipient = this.recipients.value;
		if (this.suggestEmail()) {
			this.notify.warning(this.suggestEmail());
			return;
		}
		if (recipient.includes('@') && recipient.includes('.')) {
			const newEmail = { email: recipient, name: recipient };
			if (this.isEmailInRecipients(recipient)) {
				this.notify.warning(
					this.translateService.instant('email_already_added', { email: recipient })
				);
				return;
			}
			this.emails.update(emails => [...emails, newEmail]);
			this.newMessageForm.patchValue({
				recipients: '',
			});
		}
	}

	removeContact(contact: { name: string; email: string }) {
		this.emails.update(emails => emails.filter(e => e !== contact));
	}

	recipientStringToEmail(
		value: string | { email: string; firstName: string; lastName: string }
	) {
		const isEmailsEmpty = this.emails().length === 0;
		const isInputEmpty =
			!value || typeof value !== 'object' ? (value as string).trim() : value.email === '';

		if (isEmailsEmpty && isInputEmpty) {
			this.recipients.setValidators([Validators.required, Validators.email]);
			return;
		}
		if (typeof value === 'object' && value.email) {
			this.emails.update(emails => [
				...emails,
				{
					email: value.email,
					name: value.lastName ? value.firstName + ' ' + value.lastName : value.firstName,
				},
			]);
			this.newMessageForm.patchValue({
				recipients: '',
			});
		}

		if (typeof value !== 'object' && value.includes(' ') && value.includes('@')) {
			const newEmails = value
				.trim()
				.split(' ')
				.filter(mail => mail !== '');
			const updatedEmails = newEmails.map(email => ({ email, name: '' }));
			this.emails.update(emails => [...emails, ...updatedEmails]);
			this.newMessageForm.patchValue({
				recipients: '',
			});
		}
	}

	populateMessageText(messageText: IMessageText) {
		if (Object.values(messageText).some(value => value !== null)) {
			this.newMessageForm.patchValue({
				subject: messageText.subject,
				text: messageText.text,
				files: messageText.files,
			});
			if (messageText.recipients) {
				this.emails.set(messageText.recipients);
			}
		}
	}

	navigateToExist(pageData: { page: CmmsPages; tableType: TableType }) {
		this.setNavigationConfigForHistory(CmmsPages.messages);

		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					tableTypeForExistingEntity: pageData.tableType,
					currentEntityForEdit: {
						files: this.newMessageForm.value.files,
					},
					editEntityType: 'MESSAGE',
				},
			})
		);
		this.store$.dispatch(
			setMessageExternalDate({
				messageText: {
					recipients: this.emails(),
					subject: this.newMessageForm.value.subject,
					text: this.newMessageForm.value.text,
					files: this.newMessageForm.value.files,
				},
			})
		);
		this.sidebarNavigation.navigateTo(pageData.page, sidebarHeaders[pageData.tableType]);
	}

	setNavigationConfigForHistory(currentPage: CmmsPages) {
		this.sidebarNavigation.setInitialComponent({
			initialComponent: RightSidebarAliasesEnum.messages,
			config: ACTIVE_PAGE_CONFIG[currentPage].sidebarConfig,
			formValue: AuUtilsFunctions.deepCopyObject(this.newMessageForm.value),
			initialPage: currentPage,
		});
	}

	viewSelected(item: Partial<IFileEntity>) {
		let currentPage: CmmsPages = CmmsPages.viewMessage;
		if (this.newMessage) {
			currentPage = CmmsPages.messages;
			this.store$.dispatch(
				setMessageExternalDate({
					messageText: {
						recipients: this.emails(),
						subject: this.newMessageForm.value.subject,
						text: this.newMessageForm.value.text,
						files: this.newMessageForm.value.files,
					},
				})
			);
		}
		this.setNavigationConfigForHistory(currentPage);
		this.sidebarService.viewCurrentEntity(NewMessageComponent, currentPage, item);
	}

	fileListChanged($event: Partial<IDroppedFile>[]) {
		this.filesService.addOnlyNewFilesToForm($event, this.newMessageForm);
	}

	filesDeleted({ ids, onSuccess }: { ids: string[]; onSuccess: () => void }) {
		this.filesService.deleteFileFromFormValue(ids, this.newMessageForm);
		onSuccess();
	}

	getFormValueFromStore() {
		this.sidebarNavigation
			.getInitialFormValueByPage(CmmsPages.messages)
			.subscribe(sideBar => {
				this.newMessageForm.patchValue({ files: sideBar.formValue.files });
			});
	}

	suggestEmail() {
		const email = this.newMessageForm.value.recipients;
		if (email && !email.includes('@')) {
			return 'Remember to add @';
		}
		if (email.includes('æ') || email.includes('ø') || email.includes('å')) {
			return 'Remember to use ae, o and a, instead of æ, ø and å';
		}
		return '';
	}

	isEmailInRecipients(email: string) {
		return this.emails().some(e => e.email === email);
	}

	protected readonly fileTypesConfig = fileTypesConfig;
}
